import { Mail } from "lucide-react";

/* -------------------------------------------------------------------------- */
/* ✅ Footer — self-contained, mobile-responsive portal footer               */
/* -------------------------------------------------------------------------- */

const socialLinks = [
  {
    label: "YouTube Channel",
    href: "https://www.youtube.com/@WurQ_Inc",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-5 h-5">
        <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31.3 31.3 0 0 0 0 12a31.3 31.3 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31.3 31.3 0 0 0 24 12a31.3 31.3 0 0 0-.5-5.8zM9.6 15.6V8.4L15.8 12l-6.2 3.6z" />
      </svg>
    ),
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/wurq.inc",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-5 h-5">
        <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.72 3.72 0 0 1-1.38-.9 3.72 3.72 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41 1.27-.06 1.65-.07 4.85-.07M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63a5.88 5.88 0 0 0-2.13 1.38A5.88 5.88 0 0 0 .63 4.14C.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.8.72 1.47 1.38 2.13a5.88 5.88 0 0 0 2.13 1.38c.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.88 5.88 0 0 0 2.13-1.38 5.88 5.88 0 0 0 1.38-2.13c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.88 5.88 0 0 0-1.38-2.13A5.88 5.88 0 0 0 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0zm0 5.84A6.16 6.16 0 1 0 12 18.16 6.16 6.16 0 0 0 12 5.84zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm7.85-10.4a1.44 1.44 0 1 1-2.88 0 1.44 1.44 0 0 1 2.88 0z" />
      </svg>
    ),
  },
];

export default function Footer() {
  return (
    <footer className="w-full bg-gray-50 border-t border-gray-200">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6 px-6 md:px-10 py-6">
        {/* Brand + copyright */}
        <p className="text-[13px] text-gray-600 text-center md:text-left order-3 md:order-1">
          © {new Date().getFullYear()} WurQ, Inc. All rights reserved.
        </p>

        {/* Support links */}
        <nav
          aria-label="Support links"
          className="flex flex-col sm:flex-row items-center gap-2 sm:gap-6 order-1 md:order-2"
        >
          <a
            href="mailto:support@wurq.io"
            className="inline-flex items-center gap-2 min-h-[44px] px-2 text-[14px] font-medium text-[#da291c] hover:text-[#b71f14] hover:underline transition"
          >
            <Mail size={16} aria-hidden="true" />
            support@wurq.io
          </a>
          <a
            href="https://support.wurq.io/support/home"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center min-h-[44px] px-2 text-[14px] font-medium text-[#da291c] hover:text-[#b71f14] hover:underline transition"
          >
            Support Articles
          </a>
          <a
            href="https://www.youtube.com/@WurQ_Inc"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center min-h-[44px] px-2 text-[14px] font-medium text-[#da291c] hover:text-[#b71f14] hover:underline transition"
          >
            Educational Materials
          </a>
        </nav>

        {/* Social icons */}
        <div className="flex items-center gap-2 order-2 md:order-3">
          {socialLinks.map((social) => (
            <a
              key={social.label}
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={social.label}
              title={social.label}
              className="inline-flex items-center justify-center w-11 h-11 rounded-md text-gray-600 hover:text-[#da291c] hover:bg-red-50 transition"
            >
              {social.icon}
            </a>
          ))}
        </div>
      </div>
    </footer>
  );
}